import { useEffect, useState } from 'react';
import type { ChangeEvent, JSX } from 'react';

import {
  LAYOUT_OPTIONS,
  applyLayout,
  followDeviceCapability,
  isMobileDevice,
  storedLayoutMode,
  setLayoutMode,
  type LayoutMode,
} from '../shared/theme/layout';

/**
 * Layout selection: automatic, full desktop, or compact.
 *
 * Automatic means "full desktop unless this is genuinely a phone or tablet" — window width is not
 * consulted, so a narrow window on a PC keeps every column. The override exists for the two cases
 * detection cannot get right on its own: somebody on a phone who needs the full board and will
 * accept sideways scrolling, and somebody on a large screen working in a narrow split who wants
 * the drawer.
 *
 * A native `<select>`, matching `ThemePicker` — keyboard accessible, screen-reader accessible, and
 * correctly behaved on touch with no work from us.
 */
export function LayoutPicker(): JSX.Element {
  const [mode, setMode] = useState<LayoutMode>(() => storedLayoutMode());

  // Re-resolve when input capability changes: a Surface undocked from its keyboard, or an iPad
  // with a trackpad attached. Rare, but the layout should follow rather than staying on whatever
  // was true at load.
  useEffect(() => followDeviceCapability(() => undefined), []);

  const onChange = (event: ChangeEvent<HTMLSelectElement>): void => {
    const next = event.target.value as LayoutMode;
    setLayoutMode(next);
    setMode(next);
  };

  return (
    <div className="theme-picker">
      <label className="theme-picker__label" htmlFor="layout-select">
        Layout
      </label>
      <select id="layout-select" className="theme-picker__select" value={mode} onChange={onChange}>
        {LAYOUT_OPTIONS.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Applies the stored layout on mount.
 *
 * Unlike the theme, this is **not** applied before first paint. It could be — the same
 * `theme-init.js` trick would work — but the cost of getting it late is a reflow rather than a
 * colour flash, and the detection needs `matchMedia` results that are cheap to read at mount. If
 * the reflow ever becomes visible, move it into the pre-paint script.
 */
export function useLayoutPreference(): void {
  useEffect(() => {
    applyLayout(storedLayoutMode());
    return followDeviceCapability(() => undefined);
  }, []);
}

/** Whether detection currently considers this a phone or tablet. Exposed for the picker's hint. */
export function detectedAsMobile(): boolean {
  return isMobileDevice();
}
